<script setup lang="ts">
import { onBeforeUnmount, ref, watch } from 'vue'

const props = defineProps({
  modelValue: {
    type: File,
    default: null,
  },
  accept: {
    type: String,
    default: 'image/png,image/jpeg,image/svg+xml',
  },
  label: {
    type: String,
    required: true,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  beforeSelect: {
    type: Function,
    default: null,
  },
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: File | null): void
  (e: 'change', value: File): void
  (e: 'before-select'): void
}>()

const fileInput = ref<HTMLInputElement | null>(null)
const isDragging = ref(false)

const previewUrl = ref('')

const updatePreview = (file: File | null) => {
  if (previewUrl.value) {
    URL.revokeObjectURL(previewUrl.value)
    previewUrl.value = ''
  }

  if (file && file.type.startsWith('image/')) {
    previewUrl.value = URL.createObjectURL(file)
  }
}

watch(
  () => props.modelValue,
  (file) => {
    updatePreview(file)
  },
  { immediate: true }
)

onBeforeUnmount(() => {
  if (previewUrl.value) {
    URL.revokeObjectURL(previewUrl.value)
  }
})

const openFilePicker = () => {
  if (props.beforeSelect && !props.beforeSelect()) {
    return
  }

  fileInput.value?.click()
}

const handleFile = (file?: File) => {
  if (!file) return

  emit('update:modelValue', file)
  emit('change', file)
}

const handleChange = (event: Event) => {
  const target = event.target as HTMLInputElement
  handleFile(target.files?.[0])
}

const handleDrop = (event: DragEvent) => {
  isDragging.value = false
  handleFile(event.dataTransfer?.files?.[0])
}

const formatFileSize = (size: number) => {
  if (size < 1024) {
    return `${size} B`
  }

  if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(1)} KB`
  }

  return `${(size / (1024 * 1024)).toFixed(1)} MB`
}

const removeFile = () => {
  emit('update:modelValue', null)

  if (fileInput.value) {
    fileInput.value.value = ''
  }
}
</script>

<template>
    <div class="w-full h-23.5 rounded-lg flex items-center justify-center transition cursor-pointer " :class="[
        props.disabled
            ? 'opacity-50 cursor-not-allowed'
            : '',
        isDragging
            ? 'border-[#1b325a] bg-[#f7f9fc]'
            : 'border-[#AAAAAA] hover:border-[#768aad]',
        !modelValue ? 'border border-dashed shadow' : ''
    ]" @click="openFilePicker" @dragover.prevent="isDragging = true" @dragleave.prevent="isDragging = false"
        @drop.prevent="handleDrop">
        <input ref="fileInput" type="file" :accept="accept" class="hidden" @change="handleChange" />

        <!-- Empty state -->
        <div v-if="!modelValue" class="flex flex-col items-center justify-center gap-3">

            <span class="text-xs text-[#6C6C6C]">
                {{ label }}
            </span>

            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M8.74777 0C8.92901 7.6429e-06 9.10411 0.0656426 9.2407 0.184767C9.37729 0.303891 9.46612 0.468446 9.49077 0.648L9.49777 0.75L9.49877 8H16.7518C16.9418 8.00006 17.1247 8.07224 17.2636 8.20197C17.4024 8.3317 17.4868 8.5093 17.4998 8.69888C17.5128 8.88846 17.4533 9.07589 17.3333 9.2233C17.2134 9.37071 17.042 9.4671 16.8538 9.493L16.7518 9.5H9.49877L9.50077 16.75C9.50046 16.9399 9.4281 17.1227 9.29829 17.2613C9.16848 17.4 8.9909 17.4842 8.80139 17.4971C8.61189 17.5099 8.42458 17.4503 8.27728 17.3304C8.12998 17.2105 8.03366 17.0392 8.00777 16.851L8.00077 16.749L7.99877 9.5H0.749772C0.559748 9.49994 0.376831 9.42776 0.23798 9.29803C0.099129 9.1683 0.0146974 8.9907 0.00174538 8.80112C-0.0112066 8.61154 0.0482868 8.42411 0.168204 8.2767C0.288121 8.12929 0.459522 8.0329 0.647772 8.007L0.749772 8H7.99977L7.99777 0.75C7.99777 0.551088 8.07679 0.360322 8.21744 0.21967C8.35809 0.0790175 8.54886 0 8.74777 0Z"
                    fill="#6C6C6C" />
            </svg>

            <!-- <span class="text-[10px] text-[#a0aabe]">
                PNG, JPG, SVG
            </span> -->
        </div>

        <!-- Uploaded state -->
        <div v-else class="relative w-full h-full flex items-center justify-start gap-1 " @click.stop>
            <!-- Remove -->
            <button type="button"
                class="absolute cursor-pointer  end-0 w-9 h-9 rounded-lg flex items-center justify-center text-[#ed145b] bg-white shadow-lg hover:shadow-md transition z-10"
                @click="removeFile">
                <svg width="17" height="18" viewBox="0 0 17 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                        d="M8.1251 6.02794e-09C8.85428 -4.69663e-05 9.55585 0.274429 10.0859 0.767124C10.6159 1.25982 10.9342 1.93338 10.9755 2.64963L10.9799 2.80864H15.5914C15.7583 2.80869 15.919 2.87108 16.041 2.98319C16.1629 3.0953 16.2371 3.24878 16.2485 3.41261C16.2598 3.57645 16.2076 3.73843 16.1023 3.86581C15.9969 3.9932 15.8464 4.07651 15.681 4.09889L15.5914 4.10494H14.8922L13.7679 15.3568C13.7115 15.9177 13.4544 16.4407 13.0423 16.8324C12.6302 17.224 12.0901 17.4588 11.5183 17.4948L11.3637 17.5H4.88648C4.3133 17.5 3.7588 17.2994 3.32207 16.9342C2.88535 16.569 2.59487 16.0629 2.50253 15.5063L2.48233 15.3559L1.35711 4.10494H0.658792C0.499595 4.10493 0.345785 4.04821 0.225807 3.94526C0.105829 3.84232 0.0277995 3.70011 0.0061487 3.54494L0 3.45679C6.71344e-06 3.30017 0.0576598 3.14884 0.162297 3.0308C0.266935 2.91276 0.411478 2.83599 0.569196 2.81469L0.658792 2.80864H5.27034C5.27034 2.06374 5.5711 1.34935 6.10648 0.822632C6.64185 0.29591 7.36797 6.02794e-09 8.1251 6.02794e-09V6.02794e-09ZM13.5685 4.10494H2.68084L3.79376 15.2289C3.81841 15.4771 3.92958 15.7093 4.10842 15.886C4.28726 16.0627 4.52275 16.1732 4.77492 16.1985L4.88648 16.2037H11.3637C11.8908 16.2037 12.3379 15.8364 12.4397 15.3378L12.4573 15.2289L13.5676 4.10494H13.5685ZM9.66228 6.48148C9.82148 6.48149 9.97529 6.53821 10.0953 6.64116C10.2152 6.7441 10.2933 6.88631 10.3149 7.04148L10.3211 7.12963V13.179C10.321 13.3432 10.2576 13.5013 10.1437 13.6213C10.0297 13.7413 9.87371 13.8143 9.70718 13.8255C9.54066 13.8366 9.37602 13.7852 9.24654 13.6816C9.11706 13.578 9.03239 13.4298 9.00964 13.2672L9.00349 13.179V7.12963C9.00349 6.95773 9.0729 6.79287 9.19645 6.67132C9.31999 6.54977 9.48756 6.48148 9.66228 6.48148ZM6.58792 6.48148C6.74712 6.48149 6.90093 6.53821 7.0209 6.64116C7.14088 6.7441 7.21891 6.88631 7.24056 7.04148L7.24671 7.12963V13.179C7.24666 13.3432 7.18325 13.5013 7.0693 13.6213C6.95535 13.7413 6.79935 13.8143 6.63282 13.8255C6.4663 13.8366 6.30166 13.7852 6.17218 13.6816C6.0427 13.578 5.95803 13.4298 5.93528 13.2672L5.92913 13.179V7.12963C5.92913 6.95773 5.99854 6.79287 6.12208 6.67132C6.24563 6.54977 6.4132 6.48148 6.58792 6.48148ZM8.1251 1.2963C7.73932 1.29631 7.36765 1.43904 7.08387 1.69614C6.80008 1.95325 6.62493 2.30594 6.59319 2.6842L6.58792 2.80864H9.66228C9.66228 2.40754 9.50033 2.02287 9.21205 1.73925C8.92377 1.45563 8.53279 1.2963 8.1251 1.2963Z"
                        fill="#6C6C6C" />
                </svg>

            </button>

            <!-- Preview -->
            <div class="w-20 h-11.25 shrink-0 rounded flex items-center justify-center overflow-hidden">
                <img v-if="previewUrl" :src="previewUrl" class="w-full h-full" alt="Preview" />

                <svg v-else class="w-7 h-7 text-[#71809b]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                        d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" />
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M14 2v6h6" />
                </svg>
            </div>

            <div class="flex flex-col items-start gap-2">
                <!-- File name -->
                <div dir="ltr" class="max-w-[76%] underline text-end font-fa text-wrap text-xs text-[#1A1A1A]"
                    :title="modelValue.name">
                    {{ modelValue.name }}
                </div>

                <!-- File size -->
                <div dir="ltr" class="text-xs font-fa text-[#6C6C6C]">
                    {{ formatFileSize(modelValue.size) }}
                </div>
            </div>

        </div>
    </div>
</template>